/**
 * The surplus dialog's answer → the engine's `surplus` option (D-073 (2), D-218 (a)): the same person's opposite
 * account (opened inside the operation with the PIN when missing, D-018, D-245 (e)), an existing account of the target
 * kind in the same currency, or a new account by name.
 */
import type { SurplusChoice } from "@faylasuf/ui";
import type { Translate } from "@faylasuf/i18n";
import type { CustomerAccountRow, CustomerKind, NewCustomerAccountSpec } from "../../api/types.js";
import { accountIdOf, balanceOf, fmt, kindLabel } from "./format.js";

export interface SurplusState {
  readonly choice: SurplusChoice;
  readonly targetId: string;
  readonly newName: string;
}

export const EMPTY_SURPLUS: SurplusState = { choice: "same_customer", targetId: "", newName: "" };

/** Old `debts.filter(d => d.type === …)` options: `name (type) - balance currency`; value = the ledger account id. */
export function surplusTargets(rows: readonly CustomerAccountRow[], excludeRowIds: readonly string[], kind: CustomerKind, cur: "IQD" | "USD", t: Translate): { id: string; label: string }[] {
  return rows
    .filter((r) => r.kind === kind && !excludeRowIds.includes(r.id))
    .map((r) => ({ id: accountIdOf(r, cur), label: `${r.name} (${kindLabel(t, r.kind)}) - ${fmt(balanceOf(r, cur))} ${cur}` }));
}

export type SurplusResolution =
  | { readonly ok: true; readonly accountId: string; readonly newAccounts: NewCustomerAccountSpec[]; readonly newName: string | null }
  | { readonly ok: false; readonly messageKey: string };

/** `owner` = the customer whose excess it is (the "same customer" of the dialog). */
export function resolveSurplus(state: SurplusState, owner: CustomerAccountRow, rows: readonly CustomerAccountRow[], targetKind: CustomerKind, cur: "IQD" | "USD"): SurplusResolution {
  if (state.choice === "same_customer") {
    const opposite = owner.oppositeAccountId ? rows.find((r) => r.id === owner.oppositeAccountId) : undefined;
    if (opposite) return { ok: true, accountId: accountIdOf(opposite, cur), newAccounts: [], newName: null };
    return { ok: true, accountId: "new:s", newAccounts: [{ ref: "s", kind: targetKind, currency: cur, personId: owner.personId }], newName: owner.name };
  }
  if (state.choice === "select_existing_account") {
    if (!state.targetId) return { ok: false, messageKey: "cust.surplus_pick_target" };
    return { ok: true, accountId: state.targetId, newAccounts: [], newName: null };
  }
  const name = state.newName.trim();
  if (!name) return { ok: false, messageKey: "cust.surplus_enter_name" };
  return { ok: true, accountId: "new:s", newAccounts: [{ ref: "s", kind: targetKind, currency: cur, name }], newName: name };
}
